import styled from 'styled-components';

export const CircleContainer = styled.div<{
  useThemeColor?: boolean;
  size?: number;
}>`
  display: inline-block;
  position: relative;
  width: ${({ size }) => (size ? `${size}px` : '40px')};
  height: ${({ size }) => (size ? `${size}px` : '40px')};

  > div {
    box-sizing: border-box;
    display: block;
    position: absolute;
    width: ${({ size }) => (size ? `${(4 * size) / 5}px` : '32px')};
    height: ${({ size }) => (size ? `${(4 * size) / 5}px` : '32px')};
    margin: 4px;
    border: ${({ size }) => (size && size < 20 ? '2px' : '4px')} solid
      ${({
        theme: {
          color: { ui3, s1 },
        },
        useThemeColor,
      }) => (useThemeColor ? s1 : ui3)};
    border-radius: 50%;
    animation: ring 1.2s cubic-bezier(0.5, 0, 0.5, 1) infinite;
    border-color: ${({
        theme: {
          color: { s1, ui3 },
        },
        useThemeColor,
      }) => (useThemeColor ? s1 : ui3)}
      transparent transparent transparent;
  }

  > div:nth-child(1) {
    animation-delay: -0.45s;
  }
  > div:nth-child(2) {
    animation-delay: -0.3s;
  }
  > div:nth-child(3) {
    animation-delay: -0.15s;
  }

  @keyframes ring {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

export const StarContainer = styled.div<{ size?: number }>`
  position: relative;
  width: ${({ size }) => (size ? `${size}px` : '20px')};
  height: ${({ size }) => (size ? `${size}px` : '20px')};
  display: flex;
  justify-content: center;
  align-items: center;

  > * {
    width: 1px;
    height: 50%;
    display: flex;
    align-items: end;
    position: absolute;
    transform-origin: top;
    margin-top: 9px;

    > * {
      width: 100%;
      height: 50%;
      background-color: black;
    }
  }

  > div:nth-child(1) {
    rotate: 45deg;
    > div {
      animation: pulse 1s infinite linear;
    }
  }
  > div:nth-child(2) {
    rotate: 90deg;
    > div {
      animation: pulse 1s -0.125s infinite linear;
    }
  }
  > div:nth-child(3) {
    rotate: 135deg;
    > div {
      animation: pulse 1s -0.25s infinite linear;
    }
  }
  > div:nth-child(4) {
    rotate: 180deg;
    > div {
      animation: pulse 1s -0.375s infinite linear;
    }
  }
  > div:nth-child(5) {
    rotate: 225deg;
    > div {
      animation: pulse 1s -0.5s infinite linear;
    }
  }
  > div:nth-child(6) {
    rotate: 270deg;
    > div {
      animation: pulse 1s -0.625s infinite linear;
    }
  }
  > div:nth-child(7) {
    rotate: 315deg;
    > div {
      animation: pulse 1s -0.75s infinite linear;
    }
  }
  > div:nth-child(8) {
    rotate: 360deg;
    > div {
      animation: pulse 1s -0.875s infinite linear;
    }
  }

  @keyframes pulse {
    50% {
      background-color: black;
    }
    to {
      background-color: rgba(0, 0, 0, 0);
    }
  }
`;

export const Orbit = styled.div<{ useThemeColor?: boolean; size?: number }>`
  --uib-size: ${({ size }) => (size ? `${size}px` : '25px')};
  --uib-speed: 1.5s;
  --uib-color: ${({
    theme: {
      color: { ui2, s1 },
    },
    useThemeColor,
  }) => (useThemeColor ? s1 : ui2)};

  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: var(--uib-size);
  width: var(--uib-size);
  margin: 0 4px 0 4px;

  &:before,
  &:after {
    content: '';
    position: absolute;
    height: 60%;
    width: 60%;
    border-radius: 50%;
    background-color: var(--uib-color);
    will-change: transform;
    flex-shrink: 0;
  }

  &:before {
    animation: orbit var(--uib-speed) linear infinite;
  }

  &:after {
    animation: orbit var(--uib-speed) linear calc(var(--uib-speed) / -2) infinite;
  }

  @keyframes orbit {
    0% {
      transform: translate(calc(var(--uib-size) * 0.5)) scale(0.73684);
      opacity: 0.65;
    }
    5% {
      transform: translate(calc(var(--uib-size) * 0.4)) scale(0.684208);
      opacity: 0.58;
    }
    10% {
      transform: translate(calc(var(--uib-size) * 0.3)) scale(0.631576);
      opacity: 0.51;
    }
    15% {
      transform: translate(calc(var(--uib-size) * 0.2)) scale(0.578944);
      opacity: 0.44;
    }
    20% {
      transform: translate(calc(var(--uib-size) * 0.1)) scale(0.526312);
      opacity: 0.37;
    }
    25% {
      transform: translate(0%) scale(0.47368);
      opacity: 0.3;
    }
    30% {
      transform: translate(calc(var(--uib-size) * -0.1)) scale(0.526312);
      opacity: 0.37;
    }
    35% {
      transform: translate(calc(var(--uib-size) * -0.2)) scale(0.578944);
      opacity: 0.44;
    }
    40% {
      transform: translate(calc(var(--uib-size) * -0.3)) scale(0.631576);
      opacity: 0.51;
    }
    45% {
      transform: translate(calc(var(--uib-size) * -0.4)) scale(0.684208);
      opacity: 0.58;
    }
    50% {
      transform: translate(calc(var(--uib-size) * -0.5)) scale(0.73684);
      opacity: 0.65;
    }
    55% {
      transform: translate(calc(var(--uib-size) * -0.4)) scale(0.789472);
      opacity: 0.72;
    }
    60% {
      transform: translate(calc(var(--uib-size) * -0.3)) scale(0.842104);
      opacity: 0.79;
    }
    65% {
      transform: translate(calc(var(--uib-size) * -0.2)) scale(0.894736);
      opacity: 0.86;
    }
    70% {
      transform: translate(calc(var(--uib-size) * -0.1)) scale(0.947368);
      opacity: 0.93;
    }
    75% {
      transform: translate(0%) scale(1);
      opacity: 1;
    }
    80% {
      transform: translate(calc(var(--uib-size) * 0.1)) scale(0.947368);
      opacity: 0.93;
    }
    85% {
      transform: translate(calc(var(--uib-size) * 0.2)) scale(0.894736);
      opacity: 0.86;
    }
    90% {
      transform: translate(calc(var(--uib-size) * 0.3)) scale(0.842104);
      opacity: 0.79;
    }
    95% {
      transform: translate(calc(var(--uib-size) * 0.4)) scale(0.789472);
      opacity: 0.72;
    }
    100% {
      transform: translate(calc(var(--uib-size) * 0.5)) scale(0.73684);
      opacity: 0.65;
    }
  }
`;
